import { computedFrom } from "aurelia-binding";
import { transient } from "aurelia-dependency-injection";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../../../../../../client-validation-system/custom-validation-renderer";
import { Lookups } from "../../../../../../../../constants/mep/mep-lookup.constants";
import { MepScenarioPlanningRequest } from "../../../../../../../../models/mep/mep-scenario-planning.models";
import { BaseMepFilterViewModel, MepLookupItem } from "../../../../../../../../models/mep/mep.models";
import { NotificationClientService, NotificationType } from "../../../../../../../../services/basics/notification-client.service";
import { MepLookupService } from "../../../../../../../../services/mep/mep-lookup.service";
import { MepLookupFiltersHandler } from "../../../../common-vm/mep-lookup-filters-handler";
import * as Enumerable from "linq";
import { useView } from "aurelia-framework";

@transient()
@useView('views/pages/modules/mep/tabs/mep-scenario-planning/partials/mep-scenario-planning-filter/mep-scenario-planning-filter.html')
export class MepScenarioPlanningFilter extends BaseMepFilterViewModel {

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.emissionTypes')
    get emissionTypes(): MepLookupItem[] {
        return this.lookupFiltersHandler.emissionTypes;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.financialYears')
    get baseLineYears(): MepLookupItem[] {
        return this.lookupFiltersHandler.financialYears;
    }

    @computedFrom('lookupFiltersHandler', 'lookupFiltersHandler.isLookupDataRefreshing')
    get isLookupDataRefreshing(): boolean {
        return this.lookupFiltersHandler.isLookupDataRefreshing;
    }

    refreshDataFunc: (request: MepScenarioPlanningRequest) => Promise<string[]>;

    //Emission Type Filter
    selectedEmissionTypes: MepLookupItem[] = [];
    selectedBaseLineYear: MepLookupItem;
    selectedForecastYear: number;

    private lastRequest: MepScenarioPlanningRequest;

    applyFiltersFunc: () => void;

    @computedFrom("isRefreshingData")
    get emissionTypeCategoryIds(): number[] {
        return Enumerable.from(this.selectedEmissionTypes.length == 0
            ? this.emissionTypes
            : this.selectedEmissionTypes)
            .where(x => x.additionalData != null)
            .select(x => x.additionalData.emissionTypeCategoryId)
            .distinct()
            .toArray();
    }

    constructor(portId: number, refreshDataFunc: (request: MepScenarioPlanningRequest) => Promise<string[]>,
        private notificationService: NotificationClientService, private mepLookupService: MepLookupService,
        private validationController: ValidationController) {
        super();
        this.portId = portId;
        this.refreshDataFunc = refreshDataFunc;
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.setupValidations();
        this.applyFiltersFunc = () => this.applyAllFilters();
        this.loadMepLookupFiltersHandler(portId);
    }

    public loadMepLookupFiltersHandler(portId: number) {
        this.lookupFiltersHandler = new MepLookupFiltersHandler(portId, this.applyFiltersFunc, this.mepLookupService);
    }

    initLookups() {
        return this.lookupFiltersHandler.initLookups([
            Lookups.EmissionTypes
        ]);
    }

    getLastRequest(): MepScenarioPlanningRequest {
        return this.lastRequest;
    }

    setDefaultFilters() {
        if (!this.selectedBaseLineYear && this.baseLineYears?.length > 0) {
            this.selectedBaseLineYear = this.baseLineYears.find(b => b.isDefault);
            this.selectedForecastYear = this.selectedBaseLineYear.id + 1;
        }

        if (this.selectedEmissionTypes?.length == 0 && this.emissionTypes?.length > 0) {
            this.selectedEmissionTypes = this.emissionTypes.filter(e => e.name == 'CO2');
        }
    }

    changeEmissionTypesFilter(selectedEmissionTypeIds: number[]) {
        this.selectedEmissionTypes = MepLookupItem.getSelectedItems(this.emissionTypes, selectedEmissionTypeIds);
    }

    onSelectedMonthsChanged() {
        if (this.isRefreshingData || this.isLookupDataRefreshing)
            return;

        const refreshLookupsPromise = this.lookupFiltersHandler.onSelectedMonthsChanged();
        if (refreshLookupsPromise) {
            refreshLookupsPromise.then(() => {
                this.resetFiltersToDefault();
            });
        }
    }

    isRefreshingData: boolean;
    async applyAllFilters() {
        const validation = await this.validationController.validate();

        if (this.isRefreshingData || this.isLookupDataRefreshing || !validation.valid)
            return;

        if (this.refreshDataFunc) {
            this.isRefreshingData = true;

            //save the last request so that other view models can use this to do other queries 
            //we need JSON.parse to create deep copy of object otherwise lastRequest is shallow copy and keeps the references to selected filters and gets updated
            this.lastRequest = JSON.parse(JSON.stringify(this.getRequest()));
            this.refreshDataFunc(this.getRequest()).then(() => {
                this.isRefreshingData = false;
                this.lookupFiltersHandler.skipRefreshOfLookupsBecauseOfInit = false;
            }).catch(e => {
                this.isRefreshingData = false;
                if (typeof e === 'string') {
                    this.notificationService.show("MEP Error", e, NotificationType.Error);
                }
                else {
                    this.notificationService.show("MEP Error", "Error refreshing data", NotificationType.Error);
                }
            });
        }
    }

    onForecastYearBlur(event: KeyboardEvent) {
        const key = event.key;

        if (isNaN(Number(key)) && key !== "Backspace" && key !== "Delete") {
            event.preventDefault();
            return false;
        }

        return true;
    }

    selectedBaseLineYearChanged() {
        this.validationController.validate();
    }

    getRequest(): MepScenarioPlanningRequest {
        return {
            portId: this.portId,
            emissionTypeIds: MepLookupItem.getIdsFromLookups(this.emissionTypes, this.selectedEmissionTypes),
            baselineYear: this.selectedBaseLineYear.id,
        };
    }

    clearAllFilters() {
        this.selectedEmissionTypes = [];
        this.selectedBaseLineYear = null;
        this.selectedForecastYear = null;
    }

    resetFiltersToDefault() {
        this.clearAllFilters();
        this.setDefaultFilters();
        this.applyAllFilters();
    }

    setupValidations() {
        ValidationRules
            .ensure('selectedForecastYear')
            .displayName('Forecast year')
            .required()
            .satisfies((o) => {
                return this.selectedBaseLineYear?.id < o && !isNaN(o);
            })
            .withMessage("Please enter a future year")
            .matches(/^\d{4}$/)
            .withMessage('Please enter a four-digit year')
            .on(this);
    }
}